import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/shallow';
import { useSelector } from 'hooks/reactRedux';
import { usePickedOrganizationalUnits } from 'hooks/usePickedOrganizationalUnits';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { ToolbarWrapper } from 'components/shared/ToolbarSet/ToolbarWrapper';

import {
  computeRange,
  toDate
} from 'components/shared/DateNavigation/DateNavigation.utils';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';

import type {
  EditAppointmentTypeResponse,
  EventResponse,
  PlanningEntityIdentifier
} from 'generated/index';
import type { SingleOrRange } from 'components/shared/DateNavigation';
import type { ToolbarAction } from 'components/shared/ToolbarSet/ToolbarSet.types';
import type { PlanningEntitySelection } from 'components/shared/PlanningEntityPicker';
import type {
  GridRowId,
  GridRowParams,
  GridRowSelectionModel
} from '@mui/x-data-grid-pro';

import {
  AppointmentsOverviewContentArea,
  AppointmentsOverviewPageContainer
} from './AppointmentsOverview.styles';
import {
  containsAppointmentInSeries,
  getAppointmentRowId,
  useColumnDefinitions
} from './AppointmentsOverview.utils';
import {
  APPOINTMENTS_OVERVIEW_SESSION_STORAGE_KEYS,
  PARTICIPANT_ENTITY_TYPES
} from './types';
import type { AppointmentsOverviewFiltersForm } from './components/AppointmentsOverviewFilters';
import type { AppointmentsOverviewParticipant } from './types';
import { useFilteredAppointments } from './hooks/useFilteredAppointments';
import { useBulkDeleteAppointments } from './hooks/useBulkDeleteAppointments';
import { useBulkEditParticipants } from './hooks/useBulkEditParticipants';
import { useBulkReExportAppointments } from './hooks/useBulkReExportAppointments';
import { useDownloadAppointments } from './hooks/useDownloadAppointments';
import { useAppointmentsFilterNames } from './hooks/useAppointmentsFilterNames';
import AppointmentRowTooltip from './components/AppointmentRowTooltip';
import AppointmentsOverviewFilters from './components/AppointmentsOverviewFilters';
import EditParticipantsDrawer from './components/EditParticipantsDialog/EditParticipantsDialog';
import RecurrenceConfirmationDialog from './components/RecurrenceConfirmationDialog/RecurrenceConfirmationDialog';

const BULK_ACTIONS_ID = 'appointments-overview-bulk-actions';
const DOWNLOAD_ID = 'appointments-overview-download';

// Outside the component: a new row component on every render makes the grid remount every row, so a
// ticked checkbox would throw the scroll position up.
const GRID_SLOTS = { row: AppointmentRowTooltip };

/** The participant the calendar handed over via session storage, if any; read once, then cleared. */
const takeHandedOverParticipant = (): PlanningEntitySelection | null => {
  const stored = window.sessionStorage.getItem(
    APPOINTMENTS_OVERVIEW_SESSION_STORAGE_KEYS.FILTERED_PARTICIPANT
  );
  if (!stored) return null;

  window.sessionStorage.removeItem(
    APPOINTMENTS_OVERVIEW_SESSION_STORAGE_KEYS.FILTERED_PARTICIPANT
  );

  try {
    const participant = JSON.parse(stored) as AppointmentsOverviewParticipant;
    const planningEntity = participant?.planningEntity;
    const isValidParticipant =
      !!planningEntity?.entityId &&
      !!planningEntity?.planningEntityType &&
      PARTICIPANT_ENTITY_TYPES.includes(planningEntity.planningEntityType);

    return isValidParticipant
      ? { kind: 'ENTITY', planningEntity, label: participant.label ?? '' }
      : null;
  } catch {
    // Ignore malformed session storage payloads.
    return null;
  }
};

// What the grid says instead of rows when nothing is fetched. The period comes first: with a period that
// is too long, picking another filter would not help.
const getEmptyStateKey = (
  namesSomething: boolean,
  isPeriodTooLong: boolean
) => {
  if (isPeriodTooLong) return 'appointmentsOverview.periodTooLongHint';
  if (!namesSomething) return 'appointmentsOverview.selectFilterHint';
  return undefined;
};

const AppointmentsOverview = () => {
  const { t } = useTranslation('common');
  const hasEditAccess = useSelector(
    ({ grants }) => grants.hasAddAppointmentAccess
  );

  const {
    selectedDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore(
    useShallow((state) => ({
      selectedDate: state.selectedDate,
      selectedDateRange: state.selectedDateRange,
      setSelectedDate: state.setSelectedDate,
      setSelectedDateRange: state.setSelectedDateRange
    }))
  );

  const period = useDateNavigationStore(useShallow((state) => state.period));
  const appliedPeriod = period ?? 'day';

  // Nothing is remembered across visits: every visit starts empty, apart from a participant handed over
  // from the calendar.
  const [selections, setSelections] = useState<PlanningEntitySelection[]>([]);
  const [activityIds, setActivityIds] = useState<string[]>([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Once on mount, in an effect rather than the initial state: reading the hand-over also clears it. The
  // page is empty without a filter, so without a hand-over the filter panel opens by itself. From the
  // effect rather than the initial state for the panel too: the popper anchors to the toolbar trigger,
  // which is only in the DOM after the first render.
  useEffect(() => {
    const handedOver = takeHandedOverParticipant();
    if (handedOver) {
      setSelections([handedOver]);
    } else {
      setIsFiltersOpen(true);
    }
  }, []);

  const handleApplyFilters = useCallback(
    (filters: AppointmentsOverviewFiltersForm) => {
      setSelections(filters.selections);
      setActivityIds(filters.activityIds);
    },
    []
  );

  const { startDate, endDate } = useMemo(() => {
    if (appliedPeriod === 'custom') {
      return computeRange(selectedDateRange ?? null, appliedPeriod);
    }
    return computeRange(selectedDate, appliedPeriod);
  }, [appliedPeriod, selectedDate, selectedDateRange]);

  // The picked plan units themselves, which the request expands to br ids.
  const { organizationalUnits } = usePickedOrganizationalUnits(
    selections.flatMap((selection) =>
      selection.kind === 'ORGANIZATIONAL_UNIT'
        ? [selection.organizationalUnitId]
        : []
    )
  );
  const {
    data = [],
    isLoading,
    isError,
    refetch,
    namesSomething,
    isPeriodTooLong
  } = useFilteredAppointments({
    startDate,
    endDate,
    selections,
    organizationalUnits,
    activityIds
  });

  // The toolbar summary's names, so the file's header names the filter as the screen does.
  const filterNames = useAppointmentsFilterNames(selections, activityIds);
  // The very filter the list above was fetched with, so the file holds the rows on screen.
  const { downloadAppointments, isDownloading } = useDownloadAppointments({
    startDate,
    endDate,
    selections,
    organizationalUnits,
    activityIds,
    filterNames
  });

  const [selectedAppointments, setSelectedAppointments] = useState<
    EventResponse[]
  >([]);

  const [editParticipantsDialogOpen, setEditParticipantsDialogOpen] =
    useState(false);
  const [deleteRecurrenceDialogOpen, setDeleteRecurrenceDialogOpen] =
    useState(false);
  const [reExportRecurrenceDialogOpen, setReExportRecurrenceDialogOpen] =
    useState(false);

  const { deleteAppointments } = useBulkDeleteAppointments({
    onSuccess: () => {
      setSelectedAppointments([]);
    }
  });

  const { reExportAppointments, isLoading: isReExporting } =
    useBulkReExportAppointments({
      onSuccess: () => {
        setSelectedAppointments([]);
      }
    });

  const { editParticipants, isLoading: isEditingParticipants } =
    useBulkEditParticipants({
      onSuccess: () => {
        setSelectedAppointments([]);
        setEditParticipantsDialogOpen(false);
      }
    });

  const selectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: 'include',
      ids: new Set<GridRowId>(
        selectedAppointments.map((a) => `${a.id}-${a.date}`)
      )
    }),
    [selectedAppointments]
  );

  // Clear the row selection when the filter context changes — for the filters, on Toepassen
  useEffect(() => {
    setSelectedAppointments([]);
  }, [selections, activityIds, startDate, endDate]);

  const handleSelectedDateChange = useCallback<(date: SingleOrRange) => void>(
    (date) => {
      if (appliedPeriod === 'custom') {
        setSelectedDateRange(Array.isArray(date) ? date : undefined);
        return;
      }

      if (Array.isArray(date) || !date) {
        setSelectedDate(null);
        return;
      }

      setSelectedDate(toDate(date));
    },
    [appliedPeriod, setSelectedDate, setSelectedDateRange]
  );

  const handleSelectionChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      const selected = data.filter((a) =>
        selectionModel.ids.has(`${a.id}-${a.date}`)
      );
      setSelectedAppointments(selected);
    },
    [data]
  );

  const handleDeleteClick = useCallback(() => {
    if (containsAppointmentInSeries(selectedAppointments)) {
      setDeleteRecurrenceDialogOpen(true);
    } else {
      deleteAppointments(selectedAppointments, 'ONLY_THIS');
    }
  }, [deleteAppointments, selectedAppointments]);

  const handleDeleteRecurrenceConfirm = useCallback(
    (editAppointmentType: EditAppointmentTypeResponse) => {
      deleteAppointments(selectedAppointments, editAppointmentType);
      setDeleteRecurrenceDialogOpen(false);
    },
    [deleteAppointments, selectedAppointments]
  );

  const handleDeleteRecurrenceCancel = useCallback(() => {
    setDeleteRecurrenceDialogOpen(false);
  }, []);

  const handleReExportClick = useCallback(() => {
    if (containsAppointmentInSeries(selectedAppointments)) {
      setReExportRecurrenceDialogOpen(true);
    } else {
      reExportAppointments(selectedAppointments, 'ONLY_THIS');
    }
  }, [reExportAppointments, selectedAppointments]);

  const handleReExportRecurrenceConfirm = useCallback(
    (editAppointmentType: EditAppointmentTypeResponse) => {
      reExportAppointments(selectedAppointments, editAppointmentType);
      setReExportRecurrenceDialogOpen(false);
    },
    [reExportAppointments, selectedAppointments]
  );

  const handleReExportRecurrenceCancel = useCallback(() => {
    setReExportRecurrenceDialogOpen(false);
  }, []);

  const handleEditParticipantsClick = useCallback(() => {
    setEditParticipantsDialogOpen(true);
  }, []);

  const handleEditParticipantsConfirm = useCallback(
    (
      addParticipants: PlanningEntityIdentifier[],
      removeParticipants: PlanningEntityIdentifier[],
      editAppointmentType: EditAppointmentTypeResponse
    ) => {
      editParticipants(
        selectedAppointments,
        addParticipants,
        removeParticipants,
        editAppointmentType
      );
    },
    [editParticipants, selectedAppointments]
  );

  const handleEditParticipantsClose = useCallback(() => {
    setEditParticipantsDialogOpen(false);
  }, []);

  const columns = useColumnDefinitions();

  const defaultDate = useMemo(
    () => (selectedDate ? selectedDate : dayjs().startOf('day').toDate()),
    [selectedDate]
  );

  return (
    <AppointmentsOverviewPageContainer>
      <AppointmentsOverviewContentArea>
        {/* One toolbar row above the grid, as on the takenlijst: the filters and their summary on the left,
            the period in the middle, the bulk actions on the right. The filter panel lies over the grid, so
            opening and closing it moves no rows; the toolbar stays out in zen mode while it is open. */}
        <ToolbarWrapper
          component={{
            beforeDatePicker: (
              <AppointmentsOverviewFilters
                selections={selections}
                activityIds={activityIds}
                onApply={handleApplyFilters}
                isOpen={isFiltersOpen}
                onOpenChange={setIsFiltersOpen}
              />
            )
          }}
          zenPinned={isFiltersOpen}
          dateNavigation={{
            allowMenuOptions: true,
            defaultDate,
            defaultDateRange: selectedDateRange,
            defaultPeriod: 'day',
            onDateChange: handleSelectedDateChange,
            options: ['day', 'week', 'month', 'custom']
          }}
          // The bulk actions on the selected rows, as a toolbar action on the right like the takenlijst's:
          // a menu behind one icon, with the number of selected rows on it.
          actions={[
            // Not gated on edit access: whoever can see the list may take it along. Rows only arrive for a
            // filter the list could load, so "no rows" also covers "no filter" and "period too long".
            {
              id: DOWNLOAD_ID,
              icon: 'download',
              label: t('appointmentsOverview.download.button'),
              tooltipLabel: t('appointmentsOverview.download.button'),
              ariaLabel: t('appointmentsOverview.download.button'),
              alignment: 'right',
              disabled: data.length === 0 || isDownloading,
              contextMenu: [
                {
                  label: t('appointmentsOverview.download.excel'),
                  onClick: () => downloadAppointments('XLSX'),
                  icon: 'file-xls'
                },
                {
                  label: t('appointmentsOverview.download.pdf'),
                  onClick: () => downloadAppointments('PDF'),
                  icon: 'file-pdf'
                }
              ]
            },
            ...(hasEditAccess
              ? ([
                  {
                    id: BULK_ACTIONS_ID,
                    icon: 'list-check',
                    label: t('actions'),
                    tooltipLabel: t('actions'),
                    ariaLabel: t('actions'),
                    alignment: 'right',
                    disabled: selectedAppointments.length === 0,
                    pulsingBadge: {
                      isActive: selectedAppointments.length > 0,
                      content: selectedAppointments.length
                    },
                    contextMenu: [
                      {
                        label: t(
                          'appointmentsOverview.editParticipants.buttonWithCount',
                          { count: selectedAppointments.length }
                        ),
                        onClick: handleEditParticipantsClick,
                        icon: 'user-pen'
                      },
                      {
                        label: t(
                          'appointmentsOverview.delete.buttonWithCount',
                          {
                            count: selectedAppointments.length
                          }
                        ),
                        onClick: handleDeleteClick,
                        icon: 'trash-can'
                      },
                      {
                        // Same action, same wording and same icon as the calendar's context menu.
                        label: t('mrp.exportAppointmentSeries'),
                        onClick: handleReExportClick,
                        icon: 'arrow-right-to-line',
                        // A run already under way must not be started a second time: each one
                        // re-sends every occurrence of every selected series to Ecaris and Reflex,
                        // and the selection is only cleared once the first run has been accepted.
                        disabled: isReExporting
                      }
                    ]
                  }
                ] satisfies ToolbarAction[])
              : [])
          ]}
        >
          <TableCRUDLayout
            columns={columns}
            rows={data}
            loading={isLoading}
            hasLoadingError={isError}
            onLoadingError={refetch}
            getRowId={getAppointmentRowId}
            translationKey={getEmptyStateKey(namesSomething, isPeriodTooLong)}
            initialState={{
              sorting: {
                sortModel: [{ field: 'appointmentDate', sort: 'asc' }]
              }
            }}
            editable={false}
            hideCreateAction
            checkboxSelection={hasEditAccess}
            isRowSelectableFromParent={(params: GridRowParams) =>
              params.row.isEditable !== false
            }
            customSelectionModel={selectionModel}
            onSelectionChange={handleSelectionChange}
            slots={GRID_SLOTS}
          />
        </ToolbarWrapper>
      </AppointmentsOverviewContentArea>

      <EditParticipantsDrawer
        open={editParticipantsDialogOpen}
        isLoading={isEditingParticipants}
        selectedAppointments={selectedAppointments}
        onConfirm={handleEditParticipantsConfirm}
        onClose={handleEditParticipantsClose}
      />

      <RecurrenceConfirmationDialog
        open={deleteRecurrenceDialogOpen}
        title={t('appointmentsOverview.delete.dialog.title', {
          count: selectedAppointments.length
        })}
        description={t('appointmentsOverview.delete.dialog.recurrenceWarning')}
        confirmLabel={t('appointmentsOverview.delete.button')}
        onConfirm={handleDeleteRecurrenceConfirm}
        onCancel={handleDeleteRecurrenceCancel}
      />

      <RecurrenceConfirmationDialog
        open={reExportRecurrenceDialogOpen}
        title={t('appointmentsOverview.export.dialog.title', {
          count: selectedAppointments.length
        })}
        description={t('appointmentsOverview.export.dialog.recurrenceWarning')}
        confirmLabel={t('mrp.exportAppointmentSeries')}
        onConfirm={handleReExportRecurrenceConfirm}
        onCancel={handleReExportRecurrenceCancel}
      />
    </AppointmentsOverviewPageContainer>
  );
};

export default AppointmentsOverview;
